import React, { FunctionComponent } from 'react'

import { useField } from 'formik'
import styled from 'styled-components'
import { SpaceProps, WidthProps } from 'styled-system'

import { PublicTextInput } from '@/atoms/input'
import { Box, Flex } from '@/atoms/layout'
import { Link } from '@/atoms/link'
import { PublicLabel } from '@/atoms/typography'
import { ErrorMessage } from '@/components/form-elements'
import { Color, Space } from '@/styles'

export interface PublicTextProps {
  label?: string
  fieldProps?: SpaceProps & WidthProps
  name?: string
  type?: 'text' | 'password' | 'tel'
  disablePaste?: boolean
  linkPath?: string
  linkText?: string
}

const PublicTextField: FunctionComponent<PublicTextProps> = ({
  label,
  fieldProps,
  name,
  type,
  disablePaste,
  linkText,
  linkPath,
}: PublicTextProps) => {
  const [field, meta] = useField(name)
  const hasError: boolean = meta.touched && !!meta.error

  return (
    <Box {...fieldProps} id={`${field.name}_field`}>
      <Box pb={Space.S8}>
        <Flex alignItems="center">
          <StyledLabel htmlFor={field.name} $withLink={!!linkText}>
            {label}
          </StyledLabel>
          {linkText && (
            <Link textProps={{ color: Color.PUBLIC_PURPLE, fontSize: '18px' }} to={linkPath}>
              {linkText}
            </Link>
          )}
        </Flex>
      </Box>

      <PublicTextInput id={field.name} hasError={hasError} type={type} disablePaste={disablePaste} {...field} />

      {hasError && <ErrorMessage error={meta.error} />}
    </Box>
  )
}

const StyledLabel = styled(PublicLabel)<{ $withLink: boolean }>`
  width: ${({ $withLink }) => ($withLink ? 'auto' : '100%')};
  margin-right: ${({ $withLink, theme }) => ($withLink ? theme.space.S16 : 0)};
  cursor: pointer;
`

PublicTextField.displayName = 'PublicTextField'

export default PublicTextField
